import { Badge, Button, Card } from '@monksflow/monks-ui';
import { ArrowUpRight, MessageCircle, Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CustomAgentChip } from '@/shared/ui/CustomAgentChip';
import { ToolBadge } from '@/shared/ui/ToolBadge';

import type { ToolCatalogCardProps } from './ToolCatalogCard.types';

/**
 * One tool in the catalog.
 *
 * The whole card is the target; the call to action in the corner is there to
 * say what activating it will do — start a chat with an agent, or open an app —
 * rather than to be the only way in. It appears on hover and focus, and trades
 * places with the state badge so the corner never holds both.
 */
export function ToolCatalogCard({ tool }: ToolCatalogCardProps) {
  const { t } = useTranslation('tools');
  const { t: tCommon } = useTranslation('common');

  const isAgent = tool.kind === 'agent';
  // The call to action names what activating the card does, which is not always
  // what the tool is: an unconfigured tool an admin can reach opens its
  // configuration rather than the tool.
  const isConfigure = tool.action === 'configure';
  const CtaIcon = isConfigure ? Settings2 : isAgent ? MessageCircle : ArrowUpRight;
  const ctaLabel = isConfigure
    ? t('toolsHub.toolCatalog.configure')
    : isAgent
      ? t('toolsHub.toolCatalog.startChat')
      : t('toolsHub.toolCatalog.openApp');

  const open = () => {
    if (tool.disabled) return;
    tool.onOpen();
  };

  return (
    <Card
      variant="outline"
      data-slot="tool-catalog-card"
      data-testid="tool-catalog-card"
      data-disabled={tool.disabled}
      role="button"
      tabIndex={tool.disabled ? -1 : 0}
      aria-label={tCommon('components.toolCard.ariaLabel', { title: tool.title })}
      aria-disabled={tool.disabled}
      title={tool.disabled ? tool.disabledTooltip : undefined}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      className={[
        // `p-md`, not the `p-card` role alias: Card pads itself with `py-6`, and
        // tailwind-merge only displaces a padding class built from a design-system
        // step, so the alias leaves the library's 24px top and bottom standing
        // beside its own 16px sides.
        'group relative gap-xl rounded-lg p-md shadow-none transition-colors outline-none',
        tool.disabled
          ? 'cursor-default bg-muted/40'
          : 'cursor-pointer border-border-light hover:border-border-strong focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-sm">
        <ToolBadge toolId={tool.id} category={tool.category} size="xl" />
        {tool.badgeLabel && (
          <Badge
            variant="secondary"
            className="transition-opacity group-hover:opacity-0 group-focus-within:opacity-0"
          >
            {tool.badgeLabel}
          </Badge>
        )}
      </div>

      <div className="flex flex-col gap-xs">
        <div className="flex flex-col gap-2xs">
          <span className="type-body-lg-strong line-clamp-2 text-foreground">{tool.title}</span>
          {tool.description && (
            <span className="type-body-md line-clamp-2 text-foreground-subtle">
              {tool.description}
            </span>
          )}
        </div>
        <div className="flex items-center gap-xs">
          <Badge variant="secondary" roundness="full">
            {isAgent ? t('toolsHub.toolCatalog.agent') : t('toolsHub.toolCatalog.app')}
          </Badge>
          {tool.visibility && <CustomAgentChip visibility={tool.visibility} />}
        </div>
      </div>

      {!tool.disabled && (
        <Button
          variant="ghost"
          size="sm"
          tabIndex={-1}
          onClick={(event) => {
            // The card behind is the real target; without this the same open
            // would run twice.
            event.stopPropagation();
            open();
          }}
          className="absolute top-card right-card opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
        >
          <CtaIcon className="size-3.5" />
          {ctaLabel}
        </Button>
      )}
    </Card>
  );
}

ToolCatalogCard.displayName = 'ToolCatalogCard';
